/**
 * Shared Forge authoring types (F1) — the studio contract + the typed SDK surface.
 *
 * The studio RENDERS replicated project/scene/instance/script rows and CALLS the
 * reducers; the gate (forge_access), provenance, fidelity bound, and cap
 * validation are all server-authoritative. `FORGE_SDK_SURFACE` is the typed
 * authoring API the editor offers — each call maps to one P5 capability.
 * (Kept separate from the P4 `forge.ts` governance types.)
 */

/** Authored scene-graph node kinds (the Roblox Instance analog). */
export const FORGE_INSTANCE_KINDS = ['part', 'model', 'light', 'script', 'folder', 'spawn', 'trigger'] as const;
export type ForgeInstanceKind = (typeof FORGE_INSTANCE_KINDS)[number];

export type ForgeStatus = 'draft' | 'compiled' | 'published';

/** The valid P5 capabilities a Forge script may request (the F2 compiler target). */
export const FORGE_VALID_CAPS = [
  'objects.read',
  'objects.write',
  'world.events.emit',
  'tickets.issue',
  'player.pulse.read',
  'economy.tip',
] as const;
export type ForgeCap = (typeof FORGE_VALID_CAPS)[number];

/**
 * The typed Forge SDK surface: each authoring call → the P5 capability it needs.
 * The editor uses this for autocomplete + to suggest which caps a script must request.
 */
export const FORGE_SDK_SURFACE: Record<string, ForgeCap> = {
  'forge.world.readObject': 'objects.read',
  'forge.world.moveObject': 'objects.write',
  'forge.world.emitEvent': 'world.events.emit',
  'forge.tickets.issue': 'tickets.issue',
  'forge.player.readPulse': 'player.pulse.read',
  'forge.economy.tip': 'economy.tip',
};
